import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_LOCALE, Locale, getLocaleMeta, isLocale, localeFromPath, stripLocale, withLocale,
} from "./config";

export type Language = Locale;

type LanguageContextType = {
  language: Locale;
  setLanguage: (lang: Locale) => void;
  t: (key: string) => string;
  isRTL: boolean;
  localizedPath: (path: string) => string;
  /** path without any locale prefix */
  basePath: string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const dictionaries = import.meta.glob("./locales/*.json");
const loaded: Partial<Record<Locale, Record<string, string>>> = {};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const language = localeFromPath(location.pathname);
  const [, forceRender] = useState(0);

  // Load the dictionary for the active locale
  useEffect(() => {
    if (language === DEFAULT_LOCALE || loaded[language]) return;
    const loader = dictionaries[`./locales/${language}.json`];
    if (!loader) return;
    let cancelled = false;
    loader().then((mod: any) => {
      if (cancelled) return;
      loaded[language] = mod.default ?? mod;
      forceRender((n) => n + 1);
    });
    return () => { cancelled = true; };
  }, [language]);

  // Document direction / lang
  useEffect(() => {
    const meta = getLocaleMeta(language);
    document.documentElement.setAttribute("dir", meta.dir);
    document.documentElement.setAttribute("lang", meta.code);
  }, [language]);

  const setLanguage = useCallback((lang: Locale) => {
    try { localStorage.setItem("beedigit.locale", lang); } catch { /* ignore */ }
    navigate(withLocale(location.pathname, lang) + location.search + location.hash);
  }, [navigate, location.pathname, location.search, location.hash]);

  const t = useCallback((key: string): string => {
    // Tolerate non-string values coming from dynamic content maps
    if (typeof key !== "string" || !key) return key;
    if (language === DEFAULT_LOCALE) return key;
    const dict = loaded[language];
    if (!dict) return key;
    return dict[key] || dict[key.replace(/\s+/g, " ").trim()] || key;
  }, [language, loaded[language]]);

  const localizedPath = useCallback(
    (path: string) => withLocale(path, language),
    [language],
  );

  // Keep the active locale when navigating through any internal link
  useEffect(() => {
    if (language === DEFAULT_LOCALE) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("/") || href.startsWith("//")) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;
      const url = new URL(href, window.location.origin);
      const pathOnly = url.pathname;
      const rest = url.search + url.hash;
      if (pathOnly.startsWith("/admin") || pathOnly.startsWith("/login")) return;
      if (isLocale(pathOnly.split("/")[1])) return;
      e.preventDefault();
      navigate(withLocale(pathOnly, language) + rest);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [language, navigate]);

  const isRTL = getLocaleMeta(language).dir === "rtl";

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, t, isRTL, localizedPath, basePath: stripLocale(location.pathname) }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

const FALLBACK: LanguageContextType = {
  language: DEFAULT_LOCALE,
  setLanguage: () => {},
  t: (k: string) => k,
  isRTL: false,
  localizedPath: (p: string) => p,
  basePath: "/",
};

export function useLanguage() {
  const context = useContext(LanguageContext);
  // Fallback (e.g. after a hot reload recreates the context) instead of blanking the page.
  if (!context) return FALLBACK;
  return context;
}
